import { useCallback, useEffect, useState } from 'react';
import {
  createChapterQuestion,
  createQuestionAnswer,
  deleteAnswer,
  deleteQuestion,
  dislikeAnswer,
  dislikeQuestion,
  getChapterQuestions,
  getQuestionAnswers,
  likeAnswer,
  likeQuestion,
  pinQuestion,
  reportQuestion,
  unpinQuestion,
  updateAnswer,
  updateQuestion,
} from '../services/questions';
import { mapQuestionsWithAnswers } from '../utils/questionMappers';

export function useChapterQuestions(chapterId, currentUserId, enabled = true) {
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  const fetchDiscussions = useCallback(async () => {
    if (!chapterId || !enabled) {
      setDiscussions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getChapterQuestions(chapterId);
      const questions = response?.data?.questions || [];
      const mapped = await mapQuestionsWithAnswers(
        questions,
        getQuestionAnswers,
        currentUserId
      );
      setDiscussions(mapped);
    } catch (e) {
      setDiscussions([]);
      setError(e?.message || 'Failed to load discussions');
    } finally {
      setLoading(false);
    }
  }, [chapterId, currentUserId, enabled]);

  useEffect(() => {
    fetchDiscussions();
  }, [fetchDiscussions]);

  const runAction = useCallback(async (action) => {
    setActionError(null);
    try {
      await action();
      await fetchDiscussions();
      return true;
    } catch (e) {
      setActionError(e?.message || 'Action failed');
      return false;
    }
  }, [fetchDiscussions]);

  const postQuestion = useCallback(
    (content) => runAction(() => createChapterQuestion(chapterId, content)),
    [chapterId, runAction]
  );

  const postAnswer = useCallback(
    (questionId, content) => runAction(() => createQuestionAnswer(questionId, content)),
    [runAction]
  );

  const editQuestion = useCallback(
    (questionId, content) => runAction(() => updateQuestion(questionId, content)),
    [runAction]
  );

  const removeQuestion = useCallback(
    (questionId) => runAction(() => deleteQuestion(questionId)),
    [runAction]
  );

  const togglePinQuestion = useCallback(
    (questionId, pinned) =>
      runAction(() => (pinned ? unpinQuestion(questionId) : pinQuestion(questionId))),
    [runAction]
  );

  const submitReport = useCallback(
    (questionId, payload) => runAction(() => reportQuestion(questionId, payload)),
    [runAction]
  );

  const reactToQuestion = useCallback(
    async (questionId, reaction) => {
      setActionError(null);
      try {
        const response =
          reaction === 'like'
            ? await likeQuestion(questionId)
            : await dislikeQuestion(questionId);

        const data = response?.data;
        if (!data) {
          await fetchDiscussions();
          return true;
        }

        setDiscussions((current) =>
          current.map((item) =>
            item.id === questionId
              ? {
                  ...item,
                  likes: data.likes_count ?? item.likes,
                  dislikes: data.dislikes_count ?? item.dislikes,
                  userReaction: data.user_reaction ?? null,
                }
              : item
          )
        );
        return true;
      } catch (e) {
        setActionError(e?.message || 'Failed to update reaction');
        return false;
      }
    },
    [fetchDiscussions]
  );

  const reactToAnswer = useCallback(
    async (questionId, answerId, reaction) => {
      setActionError(null);
      try {
        const response =
          reaction === 'like' ? await likeAnswer(answerId) : await dislikeAnswer(answerId);

        const data = response?.data;
        if (!data) {
          await fetchDiscussions();
          return true;
        }

        setDiscussions((current) =>
          current.map((question) => {
            if (question.id !== questionId) return question;
            return {
              ...question,
              repliesList: question.repliesList.map((answer) =>
                answer.id === answerId
                  ? {
                      ...answer,
                      likes: data.likes_count ?? answer.likes,
                      dislikes: data.dislikes_count ?? answer.dislikes,
                      userReaction: data.user_reaction ?? null,
                    }
                  : answer
              ),
            };
          })
        );
        return true;
      } catch (e) {
        setActionError(e?.message || 'Failed to update reaction');
        return false;
      }
    },
    [fetchDiscussions]
  );

  const editAnswer = useCallback(
    (answerId, content) => runAction(() => updateAnswer(answerId, content)),
    [runAction]
  );

  const removeAnswer = useCallback(
    (answerId) => runAction(() => deleteAnswer(answerId)),
    [runAction]
  );

  return {
    discussions,
    loading,
    error,
    actionError,
    clearActionError: () => setActionError(null),
    refetch: fetchDiscussions,
    postQuestion,
    postAnswer,
    editQuestion,
    removeQuestion,
    togglePinQuestion,
    submitReport,
    reactToQuestion,
    reactToAnswer,
    editAnswer,
    removeAnswer,
  };
}
